import { configureStore, ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { persistStore, persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { DEV } from '@shared/config/env';

import type { ThunkExtra } from '@shared/store';

import { apolloClient, setAppDispatch } from 'application/providers/DataProvider/DataProvider';
import { configFlagsApolloClient } from 'application/providers/DataProvider/configFlagsApolloClient';

import { createAcceptanceAdvancedListener } from '@entities/acceptanceAdvanced';
import { createGlobalPollingListener } from '@shared/lib/globalPolling';
import { createAppInitListener, setPersistor } from './application/appInitListener';
import { createConfigFlagsListener } from './listeners/configFlagsListener';
import { createRealtimeListener } from './listeners/realtimeListener';
import { createUploadsListener } from './listeners/uploadsListener';

import { rootReducer } from './rootReducer';

const persistConfig = {
  key: 'root',
  storage,
  whitelist: ['account'],
  version: 1,
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

const extra: ThunkExtra = { apolloClient, configFlagsApolloClient, uploadFiles: new Map() };

const acceptanceAdvancedListener = createAcceptanceAdvancedListener(apolloClient);
const appInitListener = createAppInitListener(apolloClient);
const configFlagsListener = createConfigFlagsListener(configFlagsApolloClient);
const globalPollingListener = createGlobalPollingListener(apolloClient);
const realtimeListener = createRealtimeListener(apolloClient);
const uploadsListener = createUploadsListener(extra.uploadFiles);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [
          FLUSH,
          REHYDRATE,
          PAUSE,
          PERSIST,
          PURGE,
          REGISTER,
          'persist/PERSIST',
          'acceptanceAdvancedSigningProcess/startAcceptanceAdvancedSigning',
          'globalPolling/startGlobalPolling',
          'account/fetchData/pending',
          'account/fetchData/fulfilled',
          'account/fetchData/rejected',
          'uploads/uploadFile/pending',
        ],
      },
      thunk: {
        extraArgument: extra,
      },
    }).prepend([
      appInitListener.middleware,
      configFlagsListener.middleware,
      globalPollingListener.middleware,
      acceptanceAdvancedListener.middleware,
      realtimeListener.middleware,
      uploadsListener.middleware,
    ]),
  devTools: DEV,
});

export const persistor = persistStore(store);
setPersistor(persistor);
setAppDispatch(store.dispatch);

export type AppStore = typeof store;
export type AppPersistor = typeof persistor;

declare global {
  type RootState = ReturnType<AppStore['getState']>;
  type AppDispatch = ThunkDispatch<RootState, ThunkExtra, UnknownAction>;
}
